import React, { useState, useEffect } from "react";
import { Bell, User, Users, ImageIcon, Mail, ChevronLeft, ChevronRight } from "lucide-react";

import useToken from "../../../../hooks/auth/useToken";
import {
  AlertBanner,
  TabButton,
  UserSelectionPanel,
  NotificationForm,
  PushNotificationForm,
  EmailForm,
  EmailFormWithTemplate,
  UnsubscribeManager,
} from "../../../../components/features/administration/notifications";

import { notificationService } from "../../../../services/administration/notificationService";
import useUsers from "../../../../hooks/administration/users/useUsers";
import NotificationUnsubscribeManager from "../../../../components/features/administration/notifications/NotificationUnsubscribeManager";

type TabType = "single" | "broadcast" | "push" | "email";
type EmailModeType = "custom" | "template" | "unsubscribe";
type PushModeType = "send" | "unsubscribe";

interface NotificationFormData {
  type: "single" | "broadcast";
  userId?: string;
  title: string;
  message: string;
  notificationType: "TRANSACTION" | "SYSTEM" | "ADMIN" | "KYC" | "PROMOTION" | "SECURITY";
  priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
  actionUrl?: string;
  actionLabel?: string;
  data?: string;
}

interface PushNotificationFormData {
  type: "single" | "broadcast";
  userIds: string[];
  title: string;
  description: string;
  image?: string;
}

interface EmailFormData {
  type: "single" | "broadcast";
  userIds: string[];
  subject: string;
  message: string;
  attachments: File[];
}

const NotificationsPage: React.FC = () => {
  const { token } = useToken();
  const { users, usersLoading } = useUsers({});

  // Navigation & UI States
  const [activeTab, setActiveTab] = useState<TabType>("single");
  const [emailMode, setEmailMode] = useState<EmailModeType>("custom");
  const [pushMode, setPushMode] = useState<PushModeType>("send");
  const [isUserPanelCollapsed, setIsUserPanelCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Form States
  const [formData, setFormData] = useState<NotificationFormData>({
    type: "single",
    title: "",
    message: "",
    notificationType: "SYSTEM",
    priority: "NORMAL",
  });

  const [pushData, setPushData] = useState<PushNotificationFormData>({
    type: "single",
    userIds: [],
    title: "",
    description: "",
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [emailData, setEmailData] = useState<EmailFormData>({
    type: "single",
    userIds: [],
    subject: "",
    message: "",
    attachments: [],
  });
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);

  // Set token dans le service
  useEffect(() => {
    if (token) {
      notificationService.setToken(token);
    }
  }, [token]);

  useEffect(() => {
    setSearchTerm("");
  }, [activeTab]);

  // --- Gestionnaires d'Upload ---
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setAlert({ type: "error", message: "L'image ne doit pas dépasser 5 MB" });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setImagePreview(base64);
      setPushData((prev) => ({ ...prev, image: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImagePreview(null);
    setPushData((prev) => ({ ...prev, image: undefined }));
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > 20 * 1024 * 1024) {
      setAlert({ type: "error", message: "Les pièces jointes ne doivent pas dépasser 20 MB au total" });
      return;
    }
    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setAlert({ type: "error", message: "Chaque fichier ne doit pas dépasser 10 MB" });
      return;
    }

    setEmailData((prev) => ({ ...prev, attachments: [...prev.attachments, ...files] }));

    files.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setAttachmentPreviews((prev) => [...prev, reader.result as string]);
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreviews((prev) => [...prev, ""]);
      }
    });
  };

  const removeAttachment = (index: number) => {
    setEmailData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
    setAttachmentPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Soumissions ---
  const handleNotificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      setAlert({ type: "error", message: "Le titre et le message sont requis" });
      return;
    }
    if (activeTab === "single" && !formData.userId) {
      setAlert({ type: "error", message: "Veuillez sélectionner un utilisateur" });
      return;
    }

    setSending(true);
    setAlert(null);
    try {
      const result = await notificationService.sendNotification({
        title: formData.title.trim(),
        message: formData.message.trim(),
        type: formData.notificationType,
        priority: formData.priority,
        userId: activeTab === "single" ? formData.userId : undefined,
        data: formData.data,
        actionUrl: formData.actionUrl,
        actionLabel: formData.actionLabel,
      });

      setAlert({
        type: "success",
        message:
          activeTab === "single"
            ? "Notification envoyée avec succès"
            : `Notification envoyée à ${result.data?.users_count || "tous les"} utilisateurs`,
      });
      setFormData((prev) => ({
        ...prev,
        title: "",
        message: "",
        data: "",
        actionUrl: "",
        actionLabel: "",
      }));
    } catch (error: any) {
      setAlert({ type: "error", message: error.message || "Erreur lors de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  const handlePushSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pushData.title.trim() || !pushData.description.trim()) {
      setAlert({ type: "error", message: "Le titre et la description sont requis" });
      return;
    }
    if (pushData.type === "single" && pushData.userIds.length === 0) {
      setAlert({ type: "error", message: "Veuillez sélectionner au moins un utilisateur" });
      return;
    }

    setSending(true);
    setAlert(null);
    try {
      const result = await notificationService.sendPushNotification({
        title: pushData.title.trim(),
        description: pushData.description.trim(),
        userIds: pushData.type === "single" ? pushData.userIds : undefined,
        broadcast: pushData.type === "broadcast",
        image: pushData.image,
      });

      setAlert({
        type: "success",
        message:
          pushData.type === "single"
            ? "Push notification envoyée avec succès"
            : `Push notification envoyée à ${result.data?.users_count || "tous les"} utilisateurs`,
      });
      setPushData({ type: "single", userIds: [], title: "", description: "" });
      clearImage();
    } catch (error: any) {
      setAlert({ type: "error", message: error.message || "Erreur lors de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  // 🔥 MODIFICATION ICI 🔥
  const handleEmailSend = async (subject: string, htmlMessage: string, attachments: File[]) => {
    setSending(true);
    setAlert(null);
    console.log("body", {
      subject: subject.trim(),
      message: htmlMessage.trim(),
      userIds: emailData.type === "single" ? emailData.userIds : undefined,
      broadcast: emailData.type === "broadcast",
      attachments,
    });
    try {
      let result;

      if (emailMode === "template") {
        console.log(htmlMessage);
        result = await notificationService.sendTemplateEmail({
          subject: subject.trim(),
          html: htmlMessage.trim(),
          userIds: emailData.type === "single" ? emailData.userIds : undefined,
          broadcast: emailData.type === "broadcast",
          attachments,
        });
      } else {
        // Mode custom : on utilise sendEmail
        console.log("body", {
          subject: subject.trim(),
          message: htmlMessage.trim(),
          userIds: emailData.type === "single" ? emailData.userIds : undefined,
          broadcast: emailData.type === "broadcast",
          attachments,
        });
        result = await notificationService.sendEmail({
          subject: subject.trim(),
          message: htmlMessage.trim(),
          userIds: emailData.type === "single" ? emailData.userIds : undefined,
          broadcast: emailData.type === "broadcast",
          attachments,
        });
      }

      setAlert({
        type: "success",
        message:
          emailData.type === "single"
            ? `Email envoyé à ${emailData.userIds.length} utilisateur(s)`
            : `Email envoyé à ${result.data?.users_count || "tous les"} utilisateurs`,
      });
      setEmailData({ type: "single", userIds: [], subject: "", message: "", attachments: [] });
      setAttachmentPreviews([]);
    } catch (error: any) {
      setAlert({ type: "error", message: error.message || "Erreur lors de l'envoi" });
    } finally {
      setSending(false);
    }
  };

  const handleLegacyEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailData.subject.trim() || !emailData.message.trim()) {
      setAlert({ type: "error", message: "Le sujet et le message sont requis" });
      return;
    }
    if (emailData.type === "single" && emailData.userIds.length === 0) {
      setAlert({ type: "error", message: "Veuillez sélectionner au moins un utilisateur" });
      return;
    }
    await handleEmailSend(emailData.subject, emailData.message, emailData.attachments);
  };

  // --- Panneau Utilisateur ---
  const renderSharedUserPanel = (
    selectedCount: number,
    selectedIds: string[],
    onSelect: (id: string) => void,
    onClear: () => void,
    onSwitchBroadcast: () => void
  ) => {
    if (isUserPanelCollapsed) {
      return (
        <div className="w-16 bg-white rounded-xl shadow-sm py-6 flex flex-col items-center border border-gray-100 min-h-[400px] h-full transition-all duration-300">
          <button
            onClick={() => setIsUserPanelCollapsed(false)}
            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-xl border border-gray-100 shadow-sm transition-colors"
            title="Agrandir le panneau"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="mt-8 flex flex-col items-center space-y-2">
            <Users className="w-5 h-5 text-gray-400" />
            <div className="bg-green-50 px-2 py-1 rounded-md text-center">
              <span className="block text-xs font-bold text-green-700">{selectedCount}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="relative bg-white border border-gray-100 rounded-xl shadow-sm transition-all duration-300 w-full">
        <button
          onClick={() => setIsUserPanelCollapsed(true)}
          className="absolute right-4 top-4 z-20 p-1.5 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors"
          title="Réduire le panneau"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <UserSelectionPanel
          title={activeTab === "single" ? "Sélectionner un utilisateur" : "Sélectionner utilisateur(s)"}
          selectedCount={selectedCount}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          users={users}
          loading={usersLoading}
          selectedIds={selectedIds}
          onSelectUser={onSelect}
          onClearAll={onClear}
          onSwitchToBroadcast={onSwitchBroadcast}
          broadcastButtonText="Envoyer à tout le monde"
        />
      </div>
    );
  };

  const showPanel =
    activeTab === "single" ||
    (activeTab === "push" && pushMode === "send" && pushData.type === "single") ||
    (activeTab === "email" && emailMode !== "unsubscribe" && emailData.type === "single");

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="flex gap-3 items-center text-3xl font-bold text-gray-900">
          <Bell className="w-8 h-8 text-green-600" />
          Notifications
        </h1>
        <p className="mt-1 text-gray-600">Envoyer des communications ciblées ou groupées</p>
      </div>

      <AlertBanner alert={alert} onClose={() => setAlert(null)} />

      {/* Navigation */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto scroller-hide">
        <TabButton
          active={activeTab === "single"}
          onClick={() => {
            setActiveTab("single");
            setFormData((prev) => ({ ...prev, type: "single", userId: undefined }));
          }}
          icon={<User className="w-5 h-5" />}
          label="Notification In-App"
        />
        <TabButton
          active={activeTab === "broadcast"}
          onClick={() => {
            setActiveTab("broadcast");
            setFormData((prev) => ({ ...prev, type: "broadcast", userId: undefined }));
          }}
          icon={<Users className="w-5 h-5" />}
          label="In-App Générale"
        />
        <TabButton
          active={activeTab === "push"}
          onClick={() => {
            setActiveTab("push");
            setPushMode("send");
            setPushData({ type: "single", userIds: [], title: "", description: "" });
          }}
          icon={<ImageIcon className="w-5 h-5" />}
          label="Push Mobile"
        />
        <TabButton
          active={activeTab === "email"}
          onClick={() => {
            setActiveTab("email");
            setEmailData({ type: "single", userIds: [], subject: "", message: "", attachments: [] });
            setEmailMode("custom");
          }}
          icon={<Mail className="w-5 h-5" />}
          label="Email"
        />
      </div>

      {/* Layout */}
      <div className="flex flex-col lg:flex-row gap-6 items-start w-full">
        {showPanel && (
          <div
            className={`transition-all duration-300 ease-in-out shrink-0 ${isUserPanelCollapsed ? "w-16" : "w-full lg:w-[360px]"} sticky top-4`}
          >
            {activeTab === "single" &&
              renderSharedUserPanel(
                formData.userId ? 1 : 0,
                formData.userId ? [formData.userId] : [],
                (userId) => setFormData((prev) => ({ ...prev, userId })),
                () => setFormData((prev) => ({ ...prev, userId: undefined })),
                () => setActiveTab("broadcast")
              )}
            {activeTab === "push" &&
              renderSharedUserPanel(
                pushData.userIds.length,
                pushData.userIds,
                (userId) =>
                  setPushData((prev) => ({
                    ...prev,
                    userIds: prev.userIds.includes(userId)
                      ? prev.userIds.filter((id) => id !== userId)
                      : [...prev.userIds, userId],
                  })),
                () => setPushData((prev) => ({ ...prev, userIds: [] })),
                () => setPushData((prev) => ({ ...prev, type: "broadcast", userIds: [] }))
              )}
            {activeTab === "email" &&
              renderSharedUserPanel(
                emailData.userIds.length,
                emailData.userIds,
                (userId) =>
                  setEmailData((prev) => ({
                    ...prev,
                    userIds: prev.userIds.includes(userId)
                      ? prev.userIds.filter((id) => id !== userId)
                      : [...prev.userIds, userId],
                  })),
                () => setEmailData((prev) => ({ ...prev, userIds: [] })),
                () => setEmailData((prev) => ({ ...prev, type: "broadcast", userIds: [] }))
              )}
          </div>
        )}

        <div className="flex-1 w-full min-w-0">
          {["single", "broadcast"].includes(activeTab) && (
            <NotificationForm
              title={formData.title}
              message={formData.message}
              notificationType={formData.notificationType}
              priority={formData.priority}
              actionUrl={formData.actionUrl}
              actionLabel={formData.actionLabel}
              data={formData.data}
              onTitleChange={(val) => setFormData((prev) => ({ ...prev, title: val }))}
              onMessageChange={(val) => setFormData((prev) => ({ ...prev, message: val }))}
              onTypeChange={(val) => setFormData((prev) => ({ ...prev, notificationType: val as any }))}
              onPriorityChange={(val) => setFormData((prev) => ({ ...prev, priority: val as any }))}
              onActionUrlChange={(val) => setFormData((prev) => ({ ...prev, actionUrl: val }))}
              onActionLabelChange={(val) => setFormData((prev) => ({ ...prev, actionLabel: val }))}
              onDataChange={(val) => setFormData((prev) => ({ ...prev, data: val }))}
              onSubmit={handleNotificationSubmit}
              sending={sending}
              isBroadcast={activeTab === "broadcast"}
            />
          )}

          {activeTab === "push" && (
            <div className="space-y-6">
              <div className="flex gap-2 border-b border-gray-100 pb-2">
                <button
                  onClick={() => setPushMode("send")}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${pushMode === "send" ? "text-green-600 bg-green-50 border-b-2 border-green-600" : "text-gray-500 hover:text-gray-800"}`}
                >
                  📱 Envoyer une push
                </button>
                <button
                  onClick={() => setPushMode("unsubscribe")}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${pushMode === "unsubscribe" ? "text-red-600 bg-red-50 border-b-2 border-red-600" : "text-gray-500 hover:text-gray-800"}`}
                >
                  🚫 Désinscrits
                </button>
              </div>

              {pushMode === "unsubscribe" ? (
                <NotificationUnsubscribeManager />
              ) : (
                <PushNotificationForm
                  title={pushData.title}
                  description={pushData.description}
                  imagePreview={imagePreview}
                  onTitleChange={(val) => setPushData((prev) => ({ ...prev, title: val }))}
                  onDescriptionChange={(val) => setPushData((prev) => ({ ...prev, description: val }))}
                  onImageUpload={handleImageUpload}
                  onClearImage={clearImage}
                  onSubmit={handlePushSubmit}
                  sending={sending}
                  userCount={pushData.userIds.length}
                  isBroadcast={pushData.type === "broadcast"}
                  onToggleMode={() => setPushData((prev) => ({ ...prev, type: "single", userIds: [] }))}
                />
              )}
            </div>
          )}

          {activeTab === "email" && (
            <div className="space-y-6">
              <div className="flex gap-2 border-b border-gray-100 pb-2">
                <button
                  onClick={() => setEmailMode("custom")}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${emailMode === "custom" ? "text-green-600 bg-green-50 border-b-2 border-green-600" : "text-gray-500 hover:text-gray-800"}`}
                >
                  📝 Message personnalisé
                </button>
                <button
                  onClick={() => setEmailMode("template")}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${emailMode === "template" ? "text-green-600 bg-green-50 border-b-2 border-green-600" : "text-gray-500 hover:text-gray-800"}`}
                >
                  🎨 Gabarit HTML
                </button>
                <button
                  onClick={() => setEmailMode("unsubscribe")}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${emailMode === "unsubscribe" ? "text-red-600 bg-red-50 border-b-2 border-red-600" : "text-gray-500 hover:text-gray-800"}`}
                >
                  🚫 Désinscrits
                </button>
              </div>

              {emailMode === "unsubscribe" ? (
                <UnsubscribeManager />
              ) : emailMode === "custom" ? (
                <EmailForm
                  subject={emailData.subject}
                  message={emailData.message}
                  attachments={emailData.attachments}
                  attachmentPreviews={attachmentPreviews}
                  onSubjectChange={(val) => setEmailData((prev) => ({ ...prev, subject: val }))}
                  onMessageChange={(val) => setEmailData((prev) => ({ ...prev, message: val }))}
                  onAttachmentUpload={handleAttachmentUpload}
                  onRemoveAttachment={removeAttachment}
                  onSubmit={handleLegacyEmailSubmit}
                  sending={sending}
                  userCount={emailData.userIds.length}
                  isBroadcast={emailData.type === "broadcast"}
                  onToggleMode={() => setEmailData((prev) => ({ ...prev, type: "single" }))}
                />
              ) : (
                <EmailFormWithTemplate
                  userCount={emailData.userIds.length}
                  isBroadcast={emailData.type === "broadcast"}
                  userIds={emailData.userIds}
                  onToggleMode={() => setEmailData((prev) => ({ ...prev, type: "single" }))}
                  onUserIdsChange={(ids) => setEmailData((prev) => ({ ...prev, userIds: ids }))}
                  onSend={handleEmailSend}
                  sending={sending}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
